import React, { useEffect, useState } from 'react';
import { api, imageUrl } from '../services/api';
import { labelFormaPago, labelTipoDocumento } from '../components/PedidoOpciones';
import FacturaPreview, { printFactura } from '../components/FacturaPreview';
import { pedidoClienteLabel } from '../utils/textEncoding';

export default function PedidoDetalle({ id, onBack, onUpdated }) {
  const [pedido, setPedido] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [showFactura, setShowFactura] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.getPedido(id);
        if (!cancelled) setPedido(res.data);
      } catch (err) {
        if (!cancelled) {
          setPedido(null);
          setError(err.message || 'No se pudo cargar el pedido');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  async function cambiarEstado(estado) {
    setSaving(true);
    setActionError('');
    try {
      const res = await api.actualizarEstado(id, estado);
      setPedido(res.data);
      onUpdated?.();
    } catch (err) {
      setActionError(err.message || 'No se pudo actualizar el estado');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p className="loading-grid">Cargando pedido...</p>;
  if (error || !pedido) {
    return (
      <section className="pedido-detalle page">
        <button type="button" className="btn-back" onClick={onBack}>
          ← Volver
        </button>
        <p className="error banner">{error || 'Pedido no encontrado'}</p>
        <button
          type="button"
          className="btn-primary"
          onClick={() => {
            setLoading(true);
            setError('');
            api
              .getPedido(id)
              .then((res) => setPedido(res.data))
              .catch((err) => setError(err.message || 'No se pudo cargar el pedido'))
              .finally(() => setLoading(false));
          }}
        >
          Reintentar
        </button>
      </section>
    );
  }

  return (
    <section className="pedido-detalle page">
      <button type="button" className="btn-back" onClick={onBack}>
        ← Volver
      </button>

      <div className="pedido-detalle-toolbar">
        <button type="button" className="btn-primary" onClick={() => setShowFactura(true)}>
          Ver factura
        </button>
      </div>

      <header className="pedido-detalle-header order-print-header">
        <img
          src="https://ohbellezaoficial.com/cdn/shop/files/Logo_OH_BELLEZA-01_110x@2x.png?v=1724283344"
          alt="¡Oh! Belleza"
          className="order-print-logo"
        />
        <div>
          <h2>{pedido.numero}</h2>
          <p>{pedidoClienteLabel(pedido)}</p>
          {pedido.es_sede || pedido.padre_codigo ? (
            <p>
              <span className="cliente-sede-badge">Sucursal</span>
              {pedido.padre_codigo ? ` Cliente ${pedido.padre_codigo}` : ''}
              {pedido.sede_etiqueta ? ` · ${pedido.sede_etiqueta}` : ''}
            </p>
          ) : null}
          {pedido.es_sede || pedido.padre_codigo
            ? null
            : pedido.sucursal_codigo
              ? <p>Cód. {pedido.sucursal_codigo}</p>
              : null}
          {pedido.sucursal_direccion ? <p>{pedido.sucursal_direccion}</p> : null}
          {pedido.cliente_telefono ? <p>{pedido.cliente_telefono}</p> : null}
          {pedido.vendedor ? <p>Vendedor: {pedido.vendedor}</p> : null}
          {pedido.forma_pago ? <p>Pago: {labelFormaPago(pedido.forma_pago)}</p> : null}
          {pedido.horario_recibo ? <p>Horario recibo: {pedido.horario_recibo}</p> : null}
        </div>
      </header>

      <div className="pedido-detalle-items order-print-items">
        {(pedido.items || []).map((it, idx) => (
          <div key={idx} className="pedido-item-row order-print-row">
            {it.imagen ? (
              <img src={imageUrl(it.imagen)} alt="" className="pedido-item-img" />
            ) : (
              <div className="pedido-item-img pedido-item-img-empty" />
            )}
            <div>
              <strong>{it.codigo}</strong>
              <span>{it.nombre}</span>
              {it.observaciones ? <em className="item-obs">Obs: {it.observaciones}</em> : null}
            </div>
            <div className="pedido-item-row-qty">
              <span>
                {it.cantidad} und{it.cantidad === 1 ? '' : 's'}
              </span>
            </div>
          </div>
        ))}
      </div>

      <p className="pedido-notas">
        <strong>Observaciones:</strong> {pedido.notas?.trim() || 'Sin observaciones'}
      </p>

      <div className="pedido-opciones-resumen">
        {pedido.transportadora ? <p><strong>Transportadora:</strong> {pedido.transportadora}</p> : null}
        {pedido.horario_recibo ? <p><strong>Horario recibo:</strong> {pedido.horario_recibo}</p> : null}
        {pedido.tipo_documento ? <p><strong>Documento:</strong> {labelTipoDocumento(pedido.tipo_documento)}</p> : null}
        {pedido.forma_pago ? <p><strong>Pago:</strong> {labelFormaPago(pedido.forma_pago)}</p> : null}
      </div>

      {actionError ? <p className="error banner">{actionError}</p> : null}
      {pedido.estado === 'pendiente' ? (
        <div className="pedido-estados-btns">
          <button
            type="button"
            className="chip danger"
            disabled={saving}
            onClick={() => cambiarEstado('cancelado')}
          >
            {saving ? 'Cancelando…' : 'Cancelar pedido'}
          </button>
        </div>
      ) : null}

      {showFactura ? (
        <div className="modal-overlay" onClick={() => setShowFactura(false)}>
          <div className="modal order-confirm factura-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="modal-close-v2"
              onClick={() => setShowFactura(false)}
              aria-label="Cerrar"
            >
              ✕
            </button>
            <div className="order-confirm-scroll" id="factura-print-area">
              <FacturaPreview
                numero={pedido.numero}
                fecha={pedido.created_at}
                pedido={pedido}
                items={pedido.items || []}
                subtotal={pedido.subtotal}
                hideMoney
                titulo={
                  pedido.tipo_documento === 'iva'
                    ? 'Factura'
                    : pedido.tipo_documento === 'remision'
                      ? 'Cotización'
                      : 'Pedido'
                }
              />
            </div>
            <div className="order-confirm-foot factura-modal-foot">
              <button type="button" className="btn-secondary" onClick={() => setShowFactura(false)}>
                Cerrar
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => printFactura('factura-print-area')}
              >
                Imprimir / PDF
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
